const ADMIN_ROLE = {
  ANY: "ANY",
  SUPERADMIN: "SUPERADMIN",
  COUNTRYADMIN: "COUNTRYADMIN",
  COUNTRYVIEWER: "COUNTRYVIEWER",
  AUDITOR: "AUDITOR",
};

const ADMIN_ROLE_HIERARCHY = {
  COUNTRYVIEWER: 1,
  COUNTRYADMIN: 2,
  AUDITOR: 3,
  SUPERADMIN: 4,
};

const ADMIN_ENVIRONMENT = {
  PREPROD: "PREPROD",
  PROD: "PROD",
};

const ADMIN_SUPERUSER = {
  email: "o2c.superadmin",
  name: "Drivvn Admin",
  roles: ["SME.O2C_PREPROD_SUPERADMIN_AC_ALL"],
  token: null,
};

const IMPORT_SOURCE_TYPES = {
  LOCAL: "local",
  SFTP: "sftp",
};

const ALLOWED_ADMIN_REFERERS = [
  "https://o2c-fr-qa.summit-automotive.solutions",
  "https://o2c-it-qa.summit-automotive.solutions",
  "https://o2c-fr-preprod.summit-automotive.solutions",
  "https://o2c-it-preprod.summit-automotive.solutions",
  "https://admin-ami-store.citroen.fr",
  "https://admin-ami-store.citroen.it",
  "https://fr-ac-b2c-green.stl.drivvn.tech",
  "https://es-ac-b2c-green.stl.drivvn.tech",
  "https://be-ac-b2c-green.stl.drivvn.tech",
  "https://lu-ac-b2c-green.stl.drivvn.tech",
  "https://gb-ac-b2c-green.stl.drivvn.tech",
  "https://it-ac-b2c-green.stl.drivvn.tech",
  "https://pt-ac-b2c-green.stl.drivvn.tech",
  "https://store.citroen.fr",
  "https://store.citroen.it",
  "https://store.citroen.be",
  "https://store.citroen.lu",
  "https://store.citroen.pt",
  "https://store.citroen.es",
  "https://store.citroen.co.uk",
];

const AVAILABLE_FILES = {
  ASSETS: "assets.csv",
  CONFIGURATION: "configuration.csv",
  DELIVERY_METHODS: "deliveryMethods.csv",
  DELIVERY_POINTS: "deliveryPoints.csv",
  DIMENSIONS: "dimensions.csv",
  DISTRIBUTION_HARBOURS: "distributionHarbours.csv",
  HOME_TEST_DRIVE_LOCATIONS: "homeTestDriveLocations.csv",
  LEAD_TIMES: "leadTime.csv",
  OPTIONS: "options.csv",
  PRECONFIGURATIONS: "preconfigurations.csv",
  REGISTRATION_REGIONS: "registrationRegions.csv",
  REGISTRATION_REGION_COSTS: "registrationRegionCosts.csv",
  ROUTES: "routes.csv",
  SERVER_CONFIGURATION: "serverConfiguration.csv",
  SERVICES: "services.csv",
  TEST_DRIVE_EXCLUSION_DATES: "testDriveExclusionDates.csv",
  TEST_DRIVE_LOCATIONS: "testDriveLocations.csv",
  TEST_DRIVE_TYPES: "testDriveTypes.csv",
  UPSELL_ITEMS: "upsellItems.csv",
  VEHICLES: "vehicles.csv",
};

const FILES_WITHOUT_DIMENSIONS = [
  AVAILABLE_FILES.REGISTRATION_REGIONS,
  AVAILABLE_FILES.REGISTRATION_REGION_COSTS,
];

const CSV_DELIMITER = ";";

const CSV_FILE_ERROR_CODES = {
  FILE_NOT_WHITELISTED: 400001,
  NO_SCHEMA_FOUND: 400002,
  INVALID_CSV_CONTENT: 400003,
  FILE_NOT_CSV: 400004,
  ERROR_READING_FILE: 400005,
  STAGED_DIR_NOT_EMPTY: 400006,
};

const CSV_FILES_BLACKLIST = [
  AVAILABLE_FILES.SERVER_CONFIGURATION,
  AVAILABLE_FILES.ASSETS,
];

const CSV_FILES_WHITELIST = Object.values(AVAILABLE_FILES).filter(item => {
  return !CSV_FILES_BLACKLIST.includes(item);
});

const SECRET_QUERY_PARAMETER = "admin-secret";
const TOKEN_QUERY_PARAMETER = "token";

const SFTP_AVAILABLE_FOLDERS = {
  CURRENT: "current",
  HISTORY: "history",
  STAGED: "staged",
};

const ADMIN_COUNTRIES_MAP = {
  FR: "France",
  IT: "Italy",
  ES: "Spain",
  BE: "Belgium",
  PT: "Portugal",
  GB: "United Kingdom",
  LU: "Luxembourg",
};

const ADMIN_SECTION_KEYS = {
  OFFER: "OFFER",
  DELIVERY: "DELIVERY",
  TESTDRIVE: "TESTDRIVE",
  CONFIG: "CONFIG",
  ASSETS: "ASSETS",
  CSV: "CSV",
  CHANGE_LOGS: "CHANGELOGS",
  RUN: "RUN",
};

const CONFIGURATION_SUBSECTION = {
  EXCLUSIONDATES: "exclusionDates",
  HARBOURS: "harbours",
  HOMELOCATIONS: "homeLocations",
  LEADTIME: "leadTime",
  LOCATIONS: "locations",
  OPTIONS: "options",
  POINTS: "points",
  PRECONFIGURATIONS: "preconfigurations",
  SERVICES: "services",
  TYPES: "types",
  VEHICLES: "vehicles",
};

const RUN_SUBSECTION = {
  DB_SEARCH: "dbSearch",
  MOP_VIEW: "mopView",
  CONFIRMATION_EMAIL_RESEND: "confirmationEmailResend",
};

const ADMIN_SECTIONS = [
  {
    label: "Offer",
    value: ADMIN_SECTION_KEYS.OFFER,
    subSections: [
      CONFIGURATION_SUBSECTION.VEHICLES,
      CONFIGURATION_SUBSECTION.LEADTIME,
      CONFIGURATION_SUBSECTION.OPTIONS,
      CONFIGURATION_SUBSECTION.PRECONFIGURATIONS,
      CONFIGURATION_SUBSECTION.SERVICES,
    ],
    enabled: false,
    minimalRole: ADMIN_ROLE.COUNTRYVIEWER,
  },
  {
    label: "Delivery",
    value: ADMIN_SECTION_KEYS.DELIVERY,
    subSections: [],
    enabled: false,
    minimalRole: ADMIN_ROLE.COUNTRYVIEWER,
  },
  {
    label: "Test Drive",
    value: ADMIN_SECTION_KEYS.TESTDRIVE,
    subSections: [],
    enabled: false,
    minimalRole: ADMIN_ROLE.COUNTRYVIEWER,
  },
  {
    label: "Configuration",
    value: ADMIN_SECTION_KEYS.CONFIG,
    subSections: [],
    enabled: false,
    minimalRole: ADMIN_ROLE.COUNTRYVIEWER,
  },
  {
    label: "Assets",
    value: ADMIN_SECTION_KEYS.ASSETS,
    subSections: [],
    enabled: true,
    minimalRole: ADMIN_ROLE.COUNTRYVIEWER,
  },
  {
    label: "Import/Export",
    value: ADMIN_SECTION_KEYS.CSV,
    subSections: [],
    enabled: true,
    minimalRole: ADMIN_ROLE.COUNTRYVIEWER,
  },
  {
    label: "Change Logs",
    value: ADMIN_SECTION_KEYS.CHANGE_LOGS,
    subSections: [],
    enabled: false,
    minimalRole: ADMIN_ROLE.COUNTRYVIEWER,
  },
  {
    label: "Run",
    value: ADMIN_SECTION_KEYS.RUN,
    subSections: [RUN_SUBSECTION.DB_SEARCH, RUN_SUBSECTION.MOP_VIEW],
    enabled: false,
    minimalRole: ADMIN_ROLE.SUPERADMIN,
  },
];

const DEFAULT_ADMIN_SECTION = ADMIN_SECTION_KEYS.CSV;

const COUNTRY_LANGUAGES_MAP = {
  BE: ["fr-BE", "nl-BE"],
  ES: ["es"],
  FR: ["fr"],
  GB: ["en"],
  IT: ["it"],
  LU: ["fr-LU"],
  PT: ["pt"],
};

const CONFIGURATION_SLICE = {
  DELIVERY: "DELIVERY",
  OFFER: "OFFER",
  TESTDRIVE: "TESTDRIVE",
};

const CONFIGURATION_SLICE_FILES = {
  [CONFIGURATION_SLICE.OFFER]: {
    [CONFIGURATION_SUBSECTION.VEHICLES]: AVAILABLE_FILES.VEHICLES,
    [CONFIGURATION_SUBSECTION.LEADTIME]: AVAILABLE_FILES.LEAD_TIMES,
    [CONFIGURATION_SUBSECTION.OPTIONS]: AVAILABLE_FILES.OPTIONS,
    [CONFIGURATION_SUBSECTION.PRECONFIGURATIONS]:
      AVAILABLE_FILES.PRECONFIGURATIONS,
    [CONFIGURATION_SUBSECTION.SERVICES]: AVAILABLE_FILES.SERVICES,
  },
  [CONFIGURATION_SLICE.TESTDRIVE]: {
    [CONFIGURATION_SUBSECTION.TYPES]: AVAILABLE_FILES.TEST_DRIVE_TYPES,
    [CONFIGURATION_SUBSECTION.LOCATIONS]:
      AVAILABLE_FILES.HOME_TEST_DRIVE_LOCATIONS,
    [CONFIGURATION_SUBSECTION.HOMELOCATIONS]:
      AVAILABLE_FILES.TEST_DRIVE_LOCATIONS,
    [CONFIGURATION_SUBSECTION.EXCLUSIONDATES]:
      AVAILABLE_FILES.TEST_DRIVE_EXCLUSION_DATES,
  },
  [CONFIGURATION_SLICE.DELIVERY]: {
    [CONFIGURATION_SUBSECTION.TYPES]: AVAILABLE_FILES.DELIVERY_METHODS,
    [CONFIGURATION_SUBSECTION.POINTS]: AVAILABLE_FILES.DELIVERY_POINTS,
    [CONFIGURATION_SUBSECTION.HARBOURS]: AVAILABLE_FILES.DISTRIBUTION_HARBOURS,
  },
};

const FORBIDDEN_SLUG_CHARS = /[*+~.()'"!:@/?&]/g;

const IMPORT_LOCK_DURATION_MINS = 10;

const IMPORT_LOCK_STATUS = {
  RUNNING: "running",
  FINISHED: "finished",
  FAILED: "failed",
  TIMED_OUT: "timed_out",
  FORCE_STOPPED: "force_stopped",
};

const CHANGE_LOGS_PER_PAGE = 5;

const RUN_TAB_LINK_EVENT = "runTabLink";

module.exports = {
  ADMIN_COUNTRIES_MAP,
  ADMIN_ENVIRONMENT,
  ADMIN_ROLE,
  ADMIN_ROLE_HIERARCHY,
  ADMIN_SECTION_KEYS,
  ADMIN_SECTIONS,
  ADMIN_SUPERUSER,
  ALLOWED_ADMIN_REFERERS,
  AVAILABLE_FILES,
  DEFAULT_ADMIN_SECTION,
  FILES_WITHOUT_DIMENSIONS,
  CHANGE_LOGS_PER_PAGE,
  CONFIGURATION_SLICE,
  CONFIGURATION_SLICE_FILES,
  CONFIGURATION_SUBSECTION,
  COUNTRY_LANGUAGES_MAP,
  CSV_DELIMITER,
  CSV_FILE_ERROR_CODES,
  CSV_FILES_BLACKLIST,
  CSV_FILES_WHITELIST,
  FORBIDDEN_SLUG_CHARS,
  IMPORT_LOCK_DURATION_MINS,
  IMPORT_LOCK_STATUS,
  IMPORT_SOURCE_TYPES,
  RUN_SUBSECTION,
  RUN_TAB_LINK_EVENT,
  SECRET_QUERY_PARAMETER,
  TOKEN_QUERY_PARAMETER,
  SFTP_AVAILABLE_FOLDERS,
};
